// src/components/Certifications.js
import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const CertificationsSection = styled.section`
    padding: 8rem 2rem;
    max-width: 1200px;
    margin: 0 auto;
`;

const SectionTitle = styled(motion.h2)`
    font-size: clamp(2rem, 4vw, 2.5rem);
    font-weight: 700;
    text-align: center;
    margin-bottom: 1rem;
    background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
`;

const SectionSubtitle = styled(motion.p)`
    text-align: center;
    color: #94a3b8;
    margin-bottom: 4rem;
    font-size: 1.1rem;
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;
`;

const CertificationsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
    gap: 2rem;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
    }
`;

const CertificationCard = styled(motion.div)`
    background: rgba(255, 255, 255, 0.03);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 16px;
    padding: 2rem;
    transition: all 0.3s ease;
    cursor: pointer;
    
    &:hover {
        background: rgba(255, 255, 255, 0.05);
        transform: translateY(-4px);
        border-color: rgba(99, 102, 241, 0.3);
        box-shadow: 0 10px 40px rgba(99, 102, 241, 0.2);
    }
`;

const CertificationTitle = styled.h3`
    font-size: 1.1rem;
    font-weight: 600;
    color: #ffffff;
    margin-bottom: 0.5rem;
    line-height: 1.4;
`;

const CertificationProvider = styled.div`
    color: #6366f1;
    font-size: 0.9rem;
    font-weight: 500;
    margin-bottom: 1rem;
`;

const CertificationBadge = styled.div`
    background: rgba(99, 102, 241, 0.1);
    color: #6366f1;
    padding: 0.5rem 1rem;
    border-radius: 20px;
    font-size: 0.8rem;
    font-weight: 500;
    display: inline-block;
    border: 1px solid rgba(99, 102, 241, 0.2);
`;

const Certifications = () => {
    const certifications = [
        {
            title: "Generative AI",
            provider: "Microsoft and LinkedIn",
            type: "Professional Certificate",
            skills: ["Generative AI", "LLMs", "AI Ethics"]
        },
        {
            title: "MongoDB Node.js Developer Path",
            provider: "SmartBridge",
            type: "Developer Certification",
            skills: ["MongoDB", "Node.js", "Database Design"]
        },
        {
            title: "Advanced LLMOps: Deploying and Managing LLMs in Production",
            provider: "Industry Expert",
            type: "Advanced Certification",
            skills: ["MLOps", "LLM Deployment", "Production AI"]
        },
        {
            title: "Journey to Cloud: Envisioning Your Solution",
            provider: "IBM",
            type: "Cloud Certification",
            skills: ["Cloud Architecture", "Solution Design", "IBM Cloud"]
        },
        {
            title: "Business Analysis",
            provider: "Microsoft and LinkedIn",
            type: "Business Certificate",
            skills: ["Business Analysis", "Requirements", "Strategy"]
        },
        {
            title: "Advanced AI: Transformers for Computer Vision",
            provider: "Industry Expert",
            type: "Technical Certification",
            skills: ["Computer Vision", "Transformers", "Deep Learning"]
        }
    ];

    return (
        <CertificationsSection>
            <SectionTitle
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
            >
                Certifications & Learning
            </SectionTitle>
            <SectionSubtitle
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                viewport={{ once: true }}
            >
                Continuous learning and professional development in cutting-edge technologies
            </SectionSubtitle>
            
            <CertificationsGrid>
                {certifications.map((cert, index) => (
                    <CertificationCard
                        key={index}
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: index * 0.1 }}
                        viewport={{ once: true }}
                        whileHover={{ scale: 1.02 }}
                    >
                        <CertificationTitle>{cert.title}</CertificationTitle>
                        <CertificationProvider>{cert.provider}</CertificationProvider>
                        <CertificationBadge>{cert.type}</CertificationBadge>
                    </CertificationCard>
                ))}
            </CertificationsGrid>
        </CertificationsSection>
    );
};

export default Certifications;
